import * as React from 'react';
import * as I18n from 'i18n-js';
import * as classNames from 'classnames';
import track from 'react-tracking';
import { get } from 'lodash';

import CoreLink from '../../components/core_link';
import SanitizedRender from '../../components/sanitized_render';
import StarRating from '../../components/star_rating';
import { IEventData, trackCSPClick } from '../../tracker';

import { CommonsCmsCspCollection } from '../../rql';
import { imageUploadURL, prefilledListingFromCSP } from '../../url_helpers';

import {
  findLowestPrice,
  NameMetaData,
  ImageMetaData,
} from '../lib/metadata_helper';

const COMPONENT_NAME = 'CspFeatureListItem';

export interface IProps {
  csp?: CommonsCmsCspCollection.Csps;
  description?: string;
  splitPrices?: boolean;
  listPosition?: number;
  hidePrices?: boolean;
  imageOverride?: string;
  useCspSummary?: boolean;
  position?: number;
  shouldLinkToSellForm?: boolean;
}

@track((props: IProps): IEventData => {
  return {
    mParticleInteraction: (data: IEventData) => trackCSPClick(data, props.csp, { displayStyle: COMPONENT_NAME }),
  };
})
export default class CspFeatureListItem extends React.Component<IProps, null> {
  getImageBackgroundStyle() {
    const img = this.props.imageOverride ?
      imageUploadURL(this.props.imageOverride) :
      this.props.csp.image.source;

    return { backgroundImage: `url(${img})` };
  }

  getLowestPrice() {
    return findLowestPrice(this.props.csp).display;
  }

  getUsedPrice() { return get(this.props.csp, 'inventory.usedLowPrice.display', null); }
  getNewPrice() {
    return get(this.props.csp, 'inventory.newLowPrice.display', null);
  }

  hasDescription() {
    const summaryFallback = this.props.useCspSummary && this.props.csp.summary;
    return !!this.props.description || !!summaryFallback;
  }

  destinationPath() {
    if (!this.props.csp) { return null; }

    if (this.props.shouldLinkToSellForm) {
      return prefilledListingFromCSP(this.props.csp.id);
    }

    return this.props.csp.webLink.path;
  }

  priceTranslationKey(format) {
    if (this.props.shouldLinkToSellForm) {
      return `cms.cspFeature.pricing.selling.${format}`;
    }

    return `cms.cspFeature.pricing.buying.${format}`;
  }

  renderPrice() {
    if (!this.getNewPrice() && !this.getUsedPrice()) return null;

    const tags = {
      tagOpen: '<b>',
      tagClose: '</b>',
    };

    if (!this.props.splitPrices) {
      return (
        <div className="csp-feature-list__item__price">
          <SanitizedRender
            html={I18n.t(
              this.priceTranslationKey('priceFromWithTags'),
              { price: this.getLowestPrice(), ...tags },
            )}
          />
        </div>
      );
    }

    return (
      <div className="csp-feature-list__item__price">
        {this.getNewPrice() &&
          <SanitizedRender
            htmlTag="div"
            html={I18n.t(
              this.priceTranslationKey('newPriceFromWithTags'),
              { price: this.getNewPrice(), ...tags },
            )}
          />
        }
        {this.getUsedPrice() &&
          <SanitizedRender
            htmlTag="div"
            className="mt-half"
            html={I18n.t(
              this.priceTranslationKey('usedPriceFromWithTags'),
              { price: this.getUsedPrice(), ...tags },
            )}
          />
        }
      </div>
    );
  }

  renderPlaceholder() {
    return (
      <div className="csp-feature-list__item csp-feature-list__item--placeholder">
        <div className="csp-feature-list__item__title-and-description">
          <div className="csp-feature-list__item__title-and-description__inner">
            <div className="csp-feature-list__item__title">
              <span className="csp-feature-list__item__number-group" />
            </div>
          </div>
        </div>
        <div className="csp-feature-list__item__details">
          <div className="csp-feature-list__item__price" />
          <div className="csp-feature-list__item__image">
            <div className="csp-feature-list__item__image__inner" />
          </div>
        </div>
      </div>
    );
  }

  renderCSPSummary(description) {
    const summaryFallback = this.props.useCspSummary && this.props.csp.summary;
    const summary = description || summaryFallback;
    if (!summary) return;

    return (
      <div className="mt-1">
        <SanitizedRender
          htmlTag="div"
          className="csp-feature-list__item__description"
          html={summary}
          truncationHeight={60}
          readMoreUrl="#"
        />
      </div>
    );
  }

  render() {
    const { csp, description, listPosition } = this.props;
    if (!csp) return this.renderPlaceholder();
    const classes = classNames(
      'csp-feature-list__item',
      { 'csp-feature-list__item--with-description': !!this.hasDescription() },
    );

    return (
      <CoreLink
        to={this.destinationPath()}
        entityId={csp.id}
        position={this.props.position}
        className={classes}
        itemScope
        itemType="http://schema.org/Product"
      >
        <div className="csp-feature-list__item__title-and-description">
          <div className="csp-feature-list__item__title-and-description__inner">
            <h3 className="csp-feature-list__item__title">
              <span className="csp-feature-list__item__number-group">
                <span className="csp-feature-list__item__number-group__text">
                  {listPosition}
                </span>
              </span>
              <span>{csp.title}</span>
            </h3>
            {!!csp.reviews && !!csp.reviews.total &&
              <StarRating
                rating={csp.reviews.avgRating / 5}
                count={csp.reviews.total}
              />
            }
            {this.renderCSPSummary(description)}
          </div>
        </div>
        <div className="csp-feature-list__item__details">
          <NameMetaData csp={csp} />
          <ImageMetaData csp={csp} />
          <div className="csp-feature-list__item__image">
            <div
              className="csp-feature-list__item__image__inner"
              style={this.getImageBackgroundStyle()}
            />
          </div>
          {!this.props.hidePrices && this.renderPrice()}
        </div>
      </CoreLink>
    );
  }
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/cms/components/csp_feature_list_item.tsx